import { useTranslation } from 'react-i18next';
import { Alert, Confirmation, Modal, PageModal } from '@otp/ui';
import {
  UpdateWttpTemplateDocument,
  WttpTemplatesDocument,
  WttpTemplateStatus,
} from 'src/graphql/generated/graphql';
import React from 'react';
import { AssignTemplateToStores } from 'src/components/wttp/templates/assign/assign';
import { useMutation } from '@apollo/client';
import { TemplateForm } from 'src/components/wttp/templates/form';
import { TemplateScheduleForm } from 'src/components/wttp/templates/schedule-form';
import { PartialTemplateType } from 'src/components/wttp/templates/types';

export enum WttpTemplateAction {
  Assign = 'assign',
  Edit = 'edit',
  Archive = 'archive',
  Unarchive = 'unarchive',
  Activate = 'activate',
  Deactivate = 'deactivate',
  SetDefault = 'set-default',
  ArchiveAlert = 'archive-alert',
  Clone = 'clone',
  Schedule = 'schedule',
}

export const ActionModals = ({
  action,
  onClose,
  wttpTemplate,
}: {
  action: WttpTemplateAction;
  onClose: () => void;
  onSubmitted?: (action: WttpTemplateAction) => void;
  wttpTemplate: PartialTemplateType;
}) => {
  const { t } = useTranslation();
  const [updateTemplate] = useMutation(UpdateWttpTemplateDocument, {
    refetchQueries: [WttpTemplatesDocument],
  });

  const onArchiveTemplateToggle = async (isActive: boolean) => {
    await updateTemplate({
      variables: {
        data: {
          isActive,
        },
        id: wttpTemplate.id,
      },
    });
    onClose();
  };

  const onActivateTemplateToggle = async (status: WttpTemplateStatus) => {
    await updateTemplate({
      variables: {
        data: {
          status,
        },
        id: wttpTemplate.id,
      },
    });
    onClose();
  };

  return (
    <>
      {action === WttpTemplateAction.Assign && (
        <Modal
          open
          onClose={onClose}
          title={t('wttp.template.store-assignment.assign-wttp')}
          fullWidth
          maxWidth="md"
        >
          <AssignTemplateToStores template={wttpTemplate} onClose={onClose} />
        </Modal>
      )}
      {action === WttpTemplateAction.Archive && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onArchiveTemplateToggle(false)}
          message={t('wttp.template.archive-confirm')}
        />
      )}
      {action === WttpTemplateAction.Unarchive && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onArchiveTemplateToggle(true)}
          message={t('wttp.template.unarchive-confirm')}
        />
      )}
      {action === WttpTemplateAction.Activate && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onActivateTemplateToggle(WttpTemplateStatus.Active)}
          message={t('wttp.template.activate-confirm')}
        />
      )}
      {action === WttpTemplateAction.Deactivate && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onActivateTemplateToggle(WttpTemplateStatus.Draft)}
          message={t('wttp.template.deactivate-confirm')}
        />
      )}
      {action === WttpTemplateAction.ArchiveAlert && (
        <Alert
          open
          onClose={onClose}
          title={t('wttp.template.archive-alert-title')}
          message={t('wttp.template.archive-alert-description')}
        />
      )}
      {action === WttpTemplateAction.Edit && (
        <TemplateForm
          onFinish={onClose}
          id={wttpTemplate.id}
          title={t('wttp.template.form.edit-template')}
        />
      )}
      {action === WttpTemplateAction.Clone && (
        <TemplateForm
          onFinish={onClose}
          id={wttpTemplate.id}
          isClone
          title={t('wttp.template.form.edit-template')}
        />
      )}
      {action === WttpTemplateAction.Schedule && (
        <TemplateScheduleForm
          title={t('wttp.template.form.schedule-template')}
          onFinish={onClose}
          id={wttpTemplate.id}
        />
      )}
    </>
  );
};
